import React, { useState, useRef } from "react";

const DAYS = [
  "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"
];

const transpose = m => m[0].map((_, i) => m.map(row => row[i]));

function timeToIndex(hour, minute) {
  return (hour - 8) * 2 + (minute === 30 ? 1 : 0);
}
function indexToTime(idx) {
  const hour = 8 + Math.floor(idx / 2);
  const minute = idx % 2 === 0 ? 0 : 30;
  return { hour, minute };
}
function slotLabel(idx) {
  const { hour, minute } = indexToTime(idx);
  return `${hour}:${minute === 0 ? "00" : "30"}`;
}
function jsonToMatrix(json) {
  const matrix = Array(6)
    .fill(0)
    .map(() => Array(22).fill(false));
  Object.entries(json).forEach(([day, ranges]) => {
    const d = parseInt(day, 10) - 1;
    ranges.forEach(range => {
      const [from, to] = range.split("-");
      const [fromH, fromM] = from.split(".").map(Number);
      const [toH, toM] = to.split(".").map(Number);
      const fromIdx = timeToIndex(fromH, fromM);
      const toIdx = timeToIndex(toH, toM);
      for (let i = fromIdx; i < toIdx; i++) {
        matrix[d][i] = true;
      }
    });
  });
  return matrix;
}
function matrixToJson(matrix) {
  const json = {};
  matrix.forEach((row, d) => {
    let ranges = [];
    let start = null;
    row.forEach((cell, i) => {
      if (cell && start === null) start = i;
      if ((!cell || i === row.length - 1) && start !== null) {
        const end = cell ? i + 1 : i;
        // Calcola orario inizio e fine slot in formato HH.MM
        const { hour: fromH, minute: fromM } = indexToTime(start);
        const { hour: toH, minute: toM } = indexToTime(end);
        const from = `${fromH}.${fromM === 0 ? "00" : "30"}`;
        const to = `${toH}.${toM === 0 ? "00" : "30"}`;
        ranges.push(`${from}-${to}`);
        start = null;
      }
    });
    if (ranges.length) json[d + 1] = ranges;
  });
  return json;
}

export default function WeekTimeAvailability({ value = {}, name, onChange, label = "Orari apertura", disabled = false, required = false }) {
  // Stato: matrice [giorno][slot], ma la UI la mostra trasposta
  const [matrix, setMatrix] = useState(() => jsonToMatrix(typeof value === "string" ? JSON.parse(value) : value));
  const [drag, setDrag] = useState(null);
  const isDragging = useRef(false);
  const dragType = useRef(null);
  const touchMoved = useRef(false);

  // Selezione rettangolare su matrice trasposta
  const applyRectSelection = (baseMatrix, startRow, startCol, endRow, endCol, value) => {
    const fromRow = Math.min(startRow, endRow);
    const toRow = Math.max(startRow, endRow);
    const fromCol = Math.min(startCol, endCol);
    const toCol = Math.max(startCol, endCol);
    return baseMatrix.map((r, rIdx) =>
      r.map((cell, cIdx) =>
        rIdx >= fromRow && rIdx <= toRow && cIdx >= fromCol && cIdx <= toCol
          ? value
          : cell
      )
    );
  };

  // Handlers per la matrice trasposta: row=slot, col=day
  const startSelection = (slot, day, type = "mouse") => {
    isDragging.current = true;
    dragType.current = type;
    const tMatrix = transpose(matrix);
    setDrag({
      startRow: slot,
      startCol: day,
      lastRow: slot,
      lastCol: day,
      value: !tMatrix[slot][day],
      baseMatrix: tMatrix.map(r => [...r]),
    });
    setMatrix(prev => {
      const tPrev = transpose(prev);
      tPrev[slot][day] = !tPrev[slot][day];
      return transpose(tPrev);
    });
    touchMoved.current = false;
  };

  const moveSelection = (slot, day) => {
    if (isDragging.current && drag) {
      const newTMatrix = applyRectSelection(
        drag.baseMatrix,
        drag.startRow,
        drag.startCol,
        slot,
        day,
        drag.value
      );
      setMatrix(transpose(newTMatrix));
      setDrag(d => ({ ...d, lastRow: slot, lastCol: day }));
    }
  };

  const endSelection = () => {
    isDragging.current = false;
    setDrag(null);
    dragType.current = null;
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: JSON.stringify(matrixToJson(matrix)),
        },
      });
    }
  };

  // Touch handlers
  const handleTouchStart = (slot, day, e) => {
    document.body.style.overflow = 'hidden';
    startSelection(slot, day, "touch");
    touchMoved.current = false;
  };
  const handleTouchMove = e => {
    e.preventDefault(); // Previene lo scroll
    if (!isDragging.current || dragType.current !== "touch") return;
    touchMoved.current = true;
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (target && target.tagName === "TD") {
      const slot = target.parentElement.rowIndex - 1; // -1 per header
      const day = target.cellIndex - 1; // -1 per colonna ora
      if (slot >= 0 && day >= 0) moveSelection(slot, day);
    }
  };
  const handleTouchEnd = (slot, day) => {
    document.body.style.overflow = '';// Ripristina lo scroll
    if (!touchMoved.current) {
      // Tap singolo: toggle solo la cella
      setMatrix(prev => {
        const tPrev = transpose(prev);
        tPrev[slot][day] = !tPrev[slot][day];
        return transpose(tPrev);
      });
      if (onChange) {
        setTimeout(() => {
          const tMatrix = transpose(matrix);
          tMatrix[slot][day] = !tMatrix[slot][day];
          onChange({
            target: {
              name: name,
              value: JSON.stringify(matrixToJson(transpose(tMatrix))),
            },
          });
        }, 0);
      }
    } else {
      endSelection();
    }
    isDragging.current = false;
    setDrag(null);
    dragType.current = null;
    touchMoved.current = false;
  };

  // Matrice trasposta per la UI: [slot][day]
  const tMatrix = transpose(matrix);


  return (
    <div
      className="week-time-availability"
      onMouseLeave={!disabled && isDragging.current ? endSelection : undefined}
      onMouseUp={!disabled && isDragging.current ? endSelection : undefined}
      onTouchMove={!disabled ? handleTouchMove : undefined}
    >
      <label>{label} {required && !disabled ? '*' : ''}</label>
      <table style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th></th>
            {DAYS.map((day, dayIdx) => (
              <th key={dayIdx}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tMatrix.map((row, slot) => (
            <tr key={slot}>
              <td>{slotLabel(slot)}</td>
              {row.map((cell, day) => (
                <td
                  key={day}
                  className={`slot ${cell ? "selected" : ""}`}
                  style={{
                    userSelect: "none",//evita problemi di scroll 
                    touchAction: "none"//evita problemi di scroll
                  }}
                  onMouseDown={!disabled ? () => startSelection(slot, day, "mouse") : undefined}
                  onMouseEnter={
                    !disabled && isDragging.current && dragType.current === "mouse"
                      ? () => moveSelection(slot, day)
                      : undefined
                  }
                  onTouchStart={!disabled ? e => handleTouchStart(slot, day, e) : undefined}
                  onTouchEnd={!disabled ? () => handleTouchEnd(slot, day) : undefined}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}